<script setup lang="ts">
import type { MaskInputOptions } from 'maska';
import { computed, reactive } from 'vue';
import Tooltip from './custom-tooltip.vue';
import { buildRulesEMail } from '../../composables/validations';
import type { Contact, PhonePrefix } from '../../types/common';

interface Props {
  phonePrefixes: PhonePrefix[];
}

const { phonePrefixes } = defineProps<Props>();

const modelValue = defineModel<Contact>({
  required: false,
  default: () => ({
    email: '',
    mobile: {
      prefix: '',
      number: '',
    },
    tel: {
      prefix: '',
      number: '',
    },
  }),
});

const maskPrefix = reactive<MaskInputOptions>({
  mask: '#######',
  eager: true,
});
const maskNumber = reactive<MaskInputOptions>({
  mask: '################',
  eager: true,
});

const email = computed<string>({
  get: () => modelValue.value.email,
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, email: value };
  },
});

const prefixMobile = computed<string>({
  get: () => modelValue.value.mobile.prefix,
  set: (prefix: string) => {
    modelValue.value = { ...modelValue.value, mobile: { ...modelValue.value.mobile, prefix } };
  },
});

const numberMobile = computed<string>({
  get: () => modelValue.value.mobile.number,
  set: (number: string) => {
    modelValue.value = { ...modelValue.value, mobile: { ...modelValue.value.mobile, number } };
  },
});

const prefixTel = computed<string>({
  get: () => modelValue.value.tel.prefix,
  set: (prefix: string) => {
    modelValue.value = { ...modelValue.value, tel: { ...modelValue.value.tel, prefix } };
  },
});

const numberTel = computed<string>({
  get: () => modelValue.value.tel.number,
  set: (number: string) => {
    modelValue.value = { ...modelValue.value, tel: { ...modelValue.value.tel, number } };
  },
});

function ruleRequired(value?: string): boolean | string {
  return (value?.length ?? 0) > 0 || 'Dieses Feld wird benötigt!';
}

function buildRuleNumberLength(min: number, max: number): (value?: string) => boolean | string {
  return (value?: string) => !value || (value?.length >= min && value?.length <= max) || `Mobilnummer muss ${min} bis ${max} Zeichen lang sein.`;
}

function ruleValidPrefix(value?: string): boolean | string {
  return !value || phonePrefixes?.some(item => item.prefix === value) || 'Ungültige Vorwahl';
}

function onPrefixTelChanged(value: string) {
  if (!value) {
    numberTel.value = '';
  }
}

function onPrefixMobileChanged(value: string) {
  if (!value) {
    numberMobile.value = '';
  }
}

function checkPrefix(prefix: string, isMobile: boolean = true) {
  if (!prefix || prefix.length < 3) {
    return false;
  }
  const number = phonePrefixes?.find(item => item.prefix === prefix);
  if (number) {
    if (isMobile) {
      return number.name === 'Mobilnummer' ? number.name : false;
    }
    return number.name !== 'Mobilnummer' ? number.name : false;
  }
  return false;
}
</script>

<template>
  <v-row no-gutters>
    <v-col cols="5" class="pr-2">
      <v-text-field
        ref="telPrefixRef"
        v-model="prefixTel"
        v-maska="maskPrefix"
        class="prefix"
        label="Vorwahl Festnetz"
        :rules="[ruleValidPrefix]"
        @change="onPrefixTelChanged"
      >
        <template #append-inner>
          <span
            v-if="checkPrefix(prefixTel, false)"
            class="text-caption font-weight-bold text-medium-emphasis"
          >
            {{ checkPrefix(prefixTel, false) }}
          </span>
          <v-icon v-else icon="mdi-phone-classic-off" />
        </template>
      </v-text-field>
    </v-col>

    <v-col cols="7" class="pl-2">
      <div class="d-flex align-center">
        <v-text-field
          v-model="numberTel"
          v-maska="maskNumber"
          label="Rufnummer Festnetz"
          :disabled="!prefixTel"
          :rules="[buildRuleNumberLength(3, 20)]"
        />

        <tooltip
          icon-class-name="ml-4 mb-4"
          class-name="w-100"
          location="left"
          message="Bitte geben Sie zuerst eine Vorwahl ein"
        />
      </div>
    </v-col>
  </v-row>

  <v-row no-gutters>
    <v-col cols="5" class="pr-2">
      <v-text-field
        ref="mobilePrefixRef"
        v-model="prefixMobile"
        v-maska="maskPrefix"
        :rules="[ruleRequired, ruleValidPrefix]"
        @change="onPrefixMobileChanged"
      >
        <template #label>
          <span>Vorwahl Mobil<sup class="required">*</sup></span>
        </template>

        <template #append-inner>
          <v-icon>
            {{ checkPrefix(prefixMobile) ? 'mdi-cellphone' : 'mdi-cellphone-off' }}
          </v-icon>
        </template>
      </v-text-field>
    </v-col>

    <v-col cols="7" class="pl-2">
      <div class="d-flex align-center">
        <v-text-field
          v-model="numberMobile"
          v-maska="maskNumber"
          :disabled="!prefixMobile"
          :rules="[ruleRequired, buildRuleNumberLength(6, 8)]"
        >
          <template #label>
            <span>Rufnummer Mobil<sup class="required">*</sup></span>
          </template>
        </v-text-field>

        <tooltip
          icon-class-name="ml-4 mb-4"
          class-name="w-100"
          location="left"
          message="Bitte geben Sie zuerst eine Vorwahl ein"
        />
      </div>
    </v-col>
  </v-row>

  <v-row no-gutters>
    <v-col cols="12">
      <v-text-field v-model="email" :rules="buildRulesEMail(true)">
        <template #label>
          <span>E-Mail<sup v-if="email" class="required">*</sup></span>
        </template>
      </v-text-field>
    </v-col>
  </v-row>
</template>
